import { surveyorSupplementPath } from '../../utils/surveyorSupplementPath';
import SurveyorAdditionalActions from '../../components/Quality/SurveyorAdditionalActions';
import { hasAdditional } from '../../components/Quality/additionalActionsUi';
import useVisibleRefresh from '../../hooks/useVisibleRefresh';
import UnreadChatBadge from '../../components/UnreadChatBadge';
import { arrivalReady } from '../../dispatchUi';
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LayoutContext } from "../../context";
import Cookies from "js-cookie";
import { useNavigate } from "react-router";

import listPlugin from '@fullcalendar/list';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { Box, Chip, CircularProgress, Stack, Typography, Card, IconButton, Button } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { APIGetSurveyorCalendarEvents } from "../../api";

import CalendarMonthRoundedIcon          from '@mui/icons-material/CalendarMonthRounded';
import ViewListRoundedIcon               from '@mui/icons-material/ViewListRounded';
import GridViewRoundedIcon               from '@mui/icons-material/GridViewRounded';
import RefreshRoundedIcon                from '@mui/icons-material/RefreshRounded';
import EventNoteRoundedIcon              from '@mui/icons-material/EventNoteRounded';
import CheckCircleOutlineRoundedIcon     from '@mui/icons-material/CheckCircleOutlineRounded';
import RadioButtonUncheckedRoundedIcon   from '@mui/icons-material/RadioButtonUncheckedRounded';
import AccessTimeRoundedIcon             from '@mui/icons-material/AccessTimeRounded';
import LocationOnRoundedIcon             from '@mui/icons-material/LocationOnRounded';
import TaskAltRoundedIcon                from '@mui/icons-material/TaskAltRounded';
import CancelRoundedIcon                 from '@mui/icons-material/CancelRounded';
import OpenInNewRoundedIcon              from '@mui/icons-material/OpenInNewRounded';
import ChatRoundedIcon                   from '@mui/icons-material/ChatRounded';
import SquareFootRoundedIcon             from '@mui/icons-material/SquareFootRounded';
import CheckCircleRoundedIcon            from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon         from '@mui/icons-material/HourglassEmptyRounded';
import DrawRoundedIcon                   from '@mui/icons-material/DrawRounded';
import WarningAmberRoundedIcon           from '@mui/icons-material/WarningAmberRounded';
import AddPhotoAlternateRoundedIcon      from '@mui/icons-material/AddPhotoAlternateRounded';
import SortRoundedIcon          from '@mui/icons-material/SortRounded';
import FilterListRoundedIcon    from '@mui/icons-material/FilterListRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon  from '@mui/icons-material/CloseRounded';

import Select                   from '@mui/joy/Select';
import Option                   from '@mui/joy/Option';
import SurveyorEventCard        from "./SurveyorEventCard";
import { useOpenEventChat }     from "../../hooks/useOpenEventChat";

// ─── Виды ─────────────────────────────────────────────────────────────────────

const VIEW_TYPES = {
    MONTH:  'dayGridMonth',
    LIST:   'listWeek',
    CARDS:  'cards',       // кастомный вид — не FullCalendar
};

// ─── Конфиг статусов карточек ─────────────────────────────────────────────────

const STATE_CONFIG = {
    completed:   { label: 'Завершено',     color: '#1F7A1F', bg: 'rgba(31,122,31,0.1)',  icon: <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> },
    in_progress: { label: 'В работе',      color: '#0f7bab', bg: 'rgba(15,123,171,0.1)', icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 13 }} /> },
    new:         { label: 'Новое',         color: '#572AC3', bg: 'rgba(87,42,195,0.1)',  icon: <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 13 }} /> },
    planned:     { label: 'Запланировано', color: '#b07d00', bg: 'rgba(176,125,0,0.1)',  icon: <CalendarMonthRoundedIcon sx={{ fontSize: 13 }} /> },
    drawing:     { label: 'На черчении',   color: '#0f7bab', bg: 'rgba(15,123,171,0.1)', icon: <DrawRoundedIcon sx={{ fontSize: 13 }} /> },
};

// ─── Вспомогательные ──────────────────────────────────────────────────────────

function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString('ru-RU', {
            day: 'numeric', month: 'short', year: 'numeric',
        });
    } catch { return dateStr; }
}

function formatTime(t) { return t ? t.slice(0, 5) : null; }

function buildTimeString(event) {
    const from = formatTime(event?.time_from);
    const to   = formatTime(event?.time_to);
    const main = formatTime(event?.time_event);
    if (from && to) return `${from} — ${to}`;
    if (main) return main;
    return null;
}

function isOverdue(event) {
    if (event?.state === 'completed' || event?.is_completed) return false;
    if (!event?.date_event) return false;
    return new Date(event.date_event) < new Date(new Date().toDateString());
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, color, bg }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 1, borderRadius: '14px',
            background: bg, border: `1.5px solid ${color}22`,
            flex: '1 1 auto', minWidth: 100, maxWidth: 170,
        }}>
            <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
            <Box>
                <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: '0.7rem' }}>
                    {label}
                </Typography>
                <Typography level="title-md" sx={{ color, fontWeight: 700, lineHeight: 1.2 }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

function StatsBar({ events }) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const total    = events.length;
    const upcoming = events.filter(e => new Date(e.date_event || e.start) >= today).length;
    const done     = events.filter(e => e.state === 'completed').length;

    return (
        <Stack direction="row" sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
            <StatCard icon={<EventNoteRoundedIcon sx={{ fontSize: 18 }} />}             label="Всего в месяце" value={total}    color="#572AC3" bg="rgba(87,42,195,0.08)" />
            <StatCard icon={<AccessTimeRoundedIcon sx={{ fontSize: 18 }} />}            label="Предстоящих"   value={upcoming} color="#0f7bab" bg="rgba(15,123,171,0.08)" />
            <StatCard icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />}   label="Завершено"     value={done}     color="#1F7A1F" bg="rgba(31,122,31,0.08)" />
        </Stack>
    );
}

// ─── EventCard ────────────────────────────────────────────────────────────────

function EventCard({ event, onComplete, onFail, onOpen, onChat, onSupplement }) {
    const navigate = useNavigate();

    const deal    = event?.bitrix_deal;
    const city    = deal?.city?.name || null;
    const address = deal?.address || null;
    const full    = [city, address].filter(Boolean).join(', ') || '—';
    const timeStr = buildTimeString(event);
    const overdue = isOverdue(event);
    const isDone  = event?.state === 'completed' || event?.is_completed;
    const canSupplement = !hasAdditional(event) && event?.is_completed && ['drawing', 'completed'].includes(event?.state);
    const hasChat = Boolean(event?.event_chat?.id);
    const cfg     = STATE_CONFIG[event?.state] || STATE_CONFIG['new'];

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.22 }}
            style={{ height: '100%' }}
        >
            <Box sx={{
                borderRadius: '18px',
                border: overdue
                    ? '1.5px solid rgba(195,0,19,0.22)'
                    : isDone
                        ? '1.5px solid rgba(31,122,31,0.18)'
                        : '1.5px solid rgba(87,42,195,0.11)',
                background: 'white',
                boxShadow: '0 2px 14px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                    boxShadow: '0 6px 28px rgba(87,42,195,0.13)',
                    transform: 'translateY(-2px)',
                },
            }}>
                {/* Полоска */}
                <Box sx={{
                    height: 4,
                    background: overdue
                        ? 'linear-gradient(90deg, #c30013, #e05060)'
                        : isDone
                            ? 'linear-gradient(90deg, #1F7A1F, #51BC51)'
                            : `linear-gradient(90deg, ${cfg.color}, ${cfg.color}99)`,
                }} />

                {/* Шапка */}
                <Box sx={{ px: 2, pt: 1.5, pb: 0.8 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: '#572AC3' }}>
                            #{event?.id} · №{deal?.id}
                        </Typography>
                        <Chip size="sm" sx={{ background: cfg.bg, color: cfg.color, fontWeight: 700, border: 'none', fontSize: '0.68rem' }}
                            startDecorator={cfg.icon}
                        >
                            {cfg.label}
                        </Chip>
                    </Stack>
                    {overdue && (
                        <Chip size="sm" sx={{ mb: 0.8, background: 'rgba(195,0,19,0.1)', color: '#c30013', fontWeight: 700, border: 'none', fontSize: '0.66rem' }}
                            startDecorator={<WarningAmberRoundedIcon sx={{ fontSize: 11 }} />}
                        >
                            Просрочено
                        </Chip>
                    )}
                </Box>

                {/* Тело */}
                <Box sx={{ px: 2, pb: 1.5, flex: 1 }}>
                    <Stack spacing={0.6}>
                        <Stack direction="row" alignItems="flex-start" spacing={0.7}>
                            <LocationOnRoundedIcon sx={{ fontSize: 14, color: '#572AC3', flexShrink: 0, mt: 0.15 }} />
                            <Typography level="body-xs" sx={{ fontWeight: 600, color: '#1a1a2e', lineHeight: 1.4 }}>
                                {full}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.7}>
                            <CalendarMonthRoundedIcon sx={{ fontSize: 13, color: 'text.tertiary' }} />
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {formatDate(event?.date_event)}
                            </Typography>
                        </Stack>
                        {timeStr && (
                            <Stack direction="row" alignItems="center" spacing={0.7}>
                                <AccessTimeRoundedIcon sx={{ fontSize: 13, color: 'text.tertiary' }} />
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{timeStr}</Typography>
                            </Stack>
                        )}
                        {deal?.square > 0 && (
                            <Stack direction="row" alignItems="center" spacing={0.7}>
                                <SquareFootRoundedIcon sx={{ fontSize: 13, color: 'text.tertiary' }} />
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{deal.square} м²</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Box>

                <Box sx={{ px: 2 }}><SurveyorAdditionalActions event={event} /></Box>
                {/* Кнопки */}
                <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: '1px solid rgba(87,42,195,0.06)', mt: 'auto' }}>
                    <Stack spacing={0.7} sx={{ mt: 1 }}>
                        {!hasAdditional(event) && !isDone && ['measuring_premises', 'additional_measuring'].includes(event?.state) && (
                            <Stack direction="row" spacing={0.7}>
                                {arrivalReady(event) && (
                                <Button size="sm" fullWidth
                                    startDecorator={<TaskAltRoundedIcon sx={{ fontSize: 14 }} />}
                                    disabled={!arrivalReady(event)}
                                    onClick={() => onComplete(event)}
                                    sx={{
                                        borderRadius: '10px', fontWeight: 700, fontSize: '0.73rem',
                                        background: 'linear-gradient(135deg, #1F7A1F, #51BC51)',
                                        color: 'white', flex: 1, py: 0.7,
                                    }}
                                >
                                    Завершить
                                </Button>
                                )}
                                <Button size="sm" variant="soft"
                                    startDecorator={<CancelRoundedIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => onFail(event)}
                                    sx={{
                                        borderRadius: '10px', fontWeight: 700, fontSize: '0.73rem',
                                        background: 'rgba(195,0,19,0.08)', color: '#c30013', py: 0.7,
                                        '&:hover': { background: 'rgba(195,0,19,0.15)' },
                                    }}
                                >
                                    Неудачная
                                </Button>
                            </Stack>
                        )}

                        {canSupplement && (
                            <Button size="sm" fullWidth variant="soft"
                                startDecorator={<AddPhotoAlternateRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onSupplement(event)}
                                sx={{
                                    borderRadius: '10px', fontWeight: 700, fontSize: '0.73rem',
                                    background: 'rgba(15,123,171,0.08)', color: '#0f7bab', py: 0.7,
                                    '&:hover': { background: 'rgba(15,123,171,0.15)' },
                                }}
                            >
                                Дополнить обмер
                            </Button>
                        )}

                        {!hasAdditional(event) && !isDone && ['measuring_premises', 'additional_measuring'].includes(event?.state) && !arrivalReady(event) && (
                            <Button fullWidth onClick={() => navigate(`/surveyor/arrive/${event.id}`)}>Я на месте</Button>
                        )}
                        <Stack direction="row" spacing={0.7}>
                            <Button size="sm" variant="outlined" color="neutral" fullWidth
                                startDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onOpen(event)}
                                sx={{ borderRadius: '10px', fontWeight: 700, fontSize: '0.73rem', flex: 1, py: 0.7 }}
                            >
                                Открыть
                            </Button>
                            {hasChat && (
                                <Button size="sm" variant="soft"
                                    startDecorator={<UnreadChatBadge chatId={event?.event_chat?.id}><ChatRoundedIcon sx={{ fontSize: 14 }} /></UnreadChatBadge>}
                                    onClick={() => onChat(event)}
                                    sx={{
                                        borderRadius: '10px', fontWeight: 700, fontSize: '0.73rem', py: 0.7,
                                        background: 'rgba(87,42,195,0.08)', color: '#572AC3',
                                        '&:hover': { background: 'rgba(87,42,195,0.15)' },
                                    }}
                                >
                                    Чат
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </motion.div>
    );
}


// ─── Фильтры и сортировка ────────────────────────────────────────────────────

const STATUS_OPTIONS = [
    { value: 'all',                 label: 'Все' },
    { value: 'measuring_premises',  label: 'В работе' },
    { value: 'drawing',             label: 'На черчении' },
    { value: 'completed',           label: 'Завершённые' },
];

const SORT_OPTIONS = [
    { value: 'date_completed_desc', label: 'Дата завершения (новые → старые)' },
    { value: 'date_completed_asc',  label: 'Дата завершения (старые → новые)' },
    { value: 'status_priority',     label: 'По статусу (сначала активные)' },
    { value: 'date_event_asc',      label: 'Дата события (ближайшие)' },
    { value: 'date_event_desc',     label: 'Дата события (поздние)' },
    { value: 'bitrix_id_asc',       label: 'Номер сделки ↑' },
    { value: 'bitrix_id_desc',      label: 'Номер сделки ↓' },
];

const STATUS_PRIORITY = {
    measuring_premises: 0,
    drawing:            1,
    new:                2,
    in_progress:        3,
    completed:          4,
};

function FilterSortBar({ filter, sort, search, onFilterChange, onSortChange, onSearchChange, totalFiltered, totalAll }) {
    const hasActiveFilter = filter !== 'all' || search.trim() !== '';

    return (
        <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Поиск */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 0.75,
                borderRadius: '14px',
                border: '1.5px solid rgba(87,42,195,0.18)',
                background: 'white',
                '&:focus-within': { borderColor: '#572AC3', boxShadow: '0 0 0 3px rgba(87,42,195,0.1)' },
                transition: 'border-color .15s, box-shadow .15s',
            }}>
                <SearchRoundedIcon sx={{ fontSize: 18, color: search ? '#572AC3' : 'text.tertiary', flexShrink: 0 }} />
                <input
                    value={search}
                    onChange={e => onSearchChange(e.target.value)}
                    placeholder="Поиск по номеру сделки, адресу, телефону, комментарию..."
                    style={{
                        flex: 1, border: 'none', outline: 'none', background: 'transparent',
                        fontSize: '0.85rem', color: 'inherit', fontFamily: 'inherit',
                    }}
                />
                {search && (
                    <Box
                        onClick={() => onSearchChange('')}
                        sx={{ cursor: 'pointer', display: 'flex', color: 'text.tertiary', '&:hover': { color: '#572AC3' } }}
                    >
                        <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </Box>
                )}
            </Box>

            {/* Фильтр по статусу + сортировка */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>

                {/* Статус — кнопки-таблетки */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                    {STATUS_OPTIONS.map(opt => (
                        <Box
                            key={opt.value}
                            onClick={() => onFilterChange(opt.value)}
                            sx={{
                                px: 1.5, py: 0.5,
                                height: 'fit-content',
                                borderRadius: '20px',
                                fontSize: '0.75rem', fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all .15s',
                                border: '1.5px solid',
                                borderColor: filter === opt.value ? '#572AC3' : 'rgba(87,42,195,0.15)',
                                background: filter === opt.value ? '#572AC3' : 'white',
                                color: filter === opt.value ? 'white' : '#572AC3',
                                '&:hover': {
                                    borderColor: '#572AC3',
                                    background: filter === opt.value ? '#4a22a8' : 'rgba(87,42,195,0.06)',
                                },
                            }}
                        >
                            {opt.label}
                        </Box>
                    ))}
                </Stack>

                {/* Сортировка */}
                <Select
                    size="sm"
                    value={sort}
                    onChange={(_, val) => onSortChange(val)}
                    startDecorator={<SortRoundedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                        minWidth: 220, borderRadius: '10px', bgcolor: 'white',
                        fontSize: '0.78rem', fontWeight: 600,
                        border: '1.5px solid rgba(87,42,195,0.18)',
                        flexShrink: 0, height: 'fit-content',
                        '&:hover': { borderColor: 'rgba(87,42,195,0.35)' },
                    }}
                >
                    {SORT_OPTIONS.map(opt => (
                        <Option key={opt.value} value={opt.value} sx={{ fontSize: '0.78rem' }}>
                            {opt.label}
                        </Option>
                    ))}
                </Select>
            </Stack>

            {/* Счётчик */}
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {hasActiveFilter
                    ? <><strong style={{ color: '#572AC3' }}>{totalFiltered}</strong> из {totalAll} событий</>
                    : <><strong style={{ color: '#572AC3' }}>{totalAll}</strong> событий</>
                }
            </Typography>
        </Box>
    );
}


// ─── Вид карточек ─────────────────────────────────────────────────────────────

function CardsView({ events, onComplete, onFail, onOpen, onChat, onSupplement }) {
    if (events.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography level="body-lg" sx={{ color: 'text.tertiary', fontWeight: 600 }}>
                    Нет событий
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
            },
            gap: 2,
        }}>
            <AnimatePresence>
                {events.map(event => (
                    <SurveyorEventCard
                        key={event.id}
                        item={event}
                        variant="full"
                        showBreakdown={true}
                        onComplete={onComplete}
                        onFail={onFail}
                        onOpen={onOpen}
                        onChat={onChat}
                        onSupplement={onSupplement}
                    />
                ))}
            </AnimatePresence>
        </Box>
    );
}


// ─── Главный компонент ────────────────────────────────────────────────────────

export default function SurveyorCalendarPage() {
    const {
        account, listEvents, setListEvents,
        setModalEvent, setCurrentDateRange,
        setCompleteModalEvent,
        setSupplementModalEvent, chatModal, setChatModal
    } = useContext(LayoutContext);
    const { openEventChat } = useOpenEventChat();

    const navigate = useNavigate();

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [allEventsLoaded, setAllEventsLoaded] = useState(false);
    // const [currentView,  setCurrentView]  = useState(isSmallScreen ? VIEW_TYPES.LIST : VIEW_TYPES.MONTH);
    const [currentView, setCurrentView]  = useState(VIEW_TYPES.CARDS);
    const [loadedMonths, setLoadedMonths] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortOrder, setSortOrder] = useState('date_completed_desc');
    const [searchQuery, setSearchQuery] = useState('');

    const calendarRef = useRef();
    const calendarRequest = useRef(0);

    const isCardsView = currentView === VIEW_TYPES.CARDS;

    // Авто-переключение при смене размера
    useEffect(() => {
        if (isSmallScreen && currentView === VIEW_TYPES.MONTH) {
            changeView(VIEW_TYPES.LIST);
        } else if (!isSmallScreen && currentView === VIEW_TYPES.LIST) {
            changeView(VIEW_TYPES.MONTH);
        }
    }, [isSmallScreen]);

    const changeView = (viewType) => {
        if (viewType === VIEW_TYPES.CARDS) {
            setCurrentView(VIEW_TYPES.CARDS);
            loadAllEvents();
            return;
        }
        // Если выходим из карточек — переключаем FullCalendar
        setCurrentView(viewType);
        // FullCalendar может ещё не быть активен, defer
        setTimeout(() => {
            const api = calendarRef.current?.getApi();
            if (api) api.changeView(viewType);
        }, 0);
    };

    const loadAllEvents = (forceReload = false, silent = false) => {
        if (!forceReload && allEventsLoaded) return;
        const requestId = ++calendarRequest.current;
        if (!silent) setIsLoading(true);
        const token = Cookies.get('auth_token_employee') || Cookies.get('auth_token');
        return APIGetSurveyorCalendarEvents(token).then((data) => {
            if (requestId !== calendarRequest.current || !Array.isArray(data)) return;
            setListEvents(data);
            setAllEventsLoaded(true);
        }).catch(error => console.error('Обновление календаря', error)).finally(() => {if (requestId === calendarRequest.current) setIsLoading(false);});
    };

    useVisibleRefresh(() => loadAllEvents(true, true), Boolean(account?.id));
    useEffect(() => {
        const refresh = () => loadAllEvents(true, true);
        window.addEventListener('surveyor-additional-changed', refresh);
        return () => window.removeEventListener('surveyor-additional-changed', refresh);
    }, [account?.id]);


    const getMonthRange = (date) => ({
        start: new Date(date.getFullYear(), date.getMonth(), 1),
        end:   new Date(date.getFullYear(), date.getMonth() + 1, 1),
    });

    const loadMonthEvents = (forceReload = false) => {
        const api = calendarRef.current?.getApi();
        if (!api) return;

        const { start, end } = getMonthRange(api.getDate());
        const startStr = start.toISOString().split('T')[0];
        const endStr   = end.toISOString().split('T')[0];
        const monthKey = `${start.getFullYear()}-${start.getMonth()}`;

        if (!forceReload && loadedMonths.has(monthKey)) return;

        const requestId = ++calendarRequest.current;
        setCurrentDateRange(startStr, endStr);
        setIsLoading(true);

        const token = Cookies.get('auth_token_employee') || Cookies.get('auth_token');

        APIGetSurveyorCalendarEvents(token, startStr, endStr).then((data) => {
            if (requestId !== calendarRequest.current || !Array.isArray(data)) return;
            setLoadedMonths(prev => {const next = new Set(prev);next.add(monthKey);return next;});
            setListEvents(prev => {
                const filtered = prev.filter(event => {
                    const d = new Date(event.date_event || event.start);
                    return d < start || d >= end;
                });
                const newMap = new Map(data.map(e => [e.id, e]));
                return [...filtered.filter(e => !newMap.has(e.id)), ...data];
            });
        }).catch(error => console.error('Обновление календаря', error)).finally(() => {if (requestId === calendarRequest.current) setIsLoading(false);});
    };

    const handleDatesSet = () => loadMonthEvents();

    useEffect(() => {
        // const timer = setTimeout(() => loadMonthEvents(), 0);
        const timer = setTimeout(() => loadAllEvents(), 0);
        return () => clearTimeout(timer);
    }, []);

    const handleRefresh = () => {
        if (isCardsView) {
            setAllEventsLoaded(false); // ← добавить
            loadAllEvents(true);
            return;
        }
        const api = calendarRef.current?.getApi();
        if (!api) return;
        const { start } = getMonthRange(api.getDate());
        const monthKey  = `${start.getFullYear()}-${start.getMonth()}`;
        setLoadedMonths(prev => { const n = new Set(prev); n.delete(monthKey); return n; });
        loadMonthEvents(true);
    };

    const handleEventClick = (info) => {
        setModalEvent({
            event: listEvents.find(item => item.id == info.event.id),
            open: true,
        });
    };

    // const handleChat = (event) => {
    //     const chatId = event?.event_chat?.id;
    //     if (chatId) navigate(`/chats?chatId=${chatId}`);
    // };

    const handleChat = (event) => openEventChat(event);

    // Иконки переключателя
    const viewButtons = [
        { type: VIEW_TYPES.CARDS, icon: <GridViewRoundedIcon sx={{ fontSize: 18 }} />,      title: 'Карточки' },
        { type: VIEW_TYPES.MONTH, icon: <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />, title: 'Календарь' },
        { type: VIEW_TYPES.LIST,  icon: <ViewListRoundedIcon sx={{ fontSize: 18 }} />,      title: 'Список' },
    ];

    const filteredAndSorted = useMemo(() => {
        let result = filterStatus === 'all'
            ? [...listEvents]
            : listEvents.filter(e => e.state === filterStatus);

        // Поиск
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(e => {
                const deal = e?.bitrix_deal;
                return (
                    String(deal?.id || '').includes(q) ||
                    String(e?.id || '').includes(q) ||
                    (deal?.address || '').toLowerCase().includes(q) ||
                    (deal?.contact_phone || '').replace(/\D/g, '').includes(q.replace(/\D/g, '')) ||
                    (deal?.contact_name || '').toLowerCase().includes(q) ||
                    (e?.comment || '').toLowerCase().includes(q) ||
                    (e?.manager_comment || '').toLowerCase().includes(q) ||
                    (deal?.city?.name || '').toLowerCase().includes(q)
                );
            });
        }

        // Сортировка
        result.sort((a, b) => {
            switch (sortOrder) {
                case 'date_completed_desc': {
                    // Незавершённые события — наверх (у них нет даты завершения)
                    const aDate = a.date_completed || a.completed_at || null;
                    const bDate = b.date_completed || b.completed_at || null;
                    if (!aDate && !bDate) return new Date(b.date_event || 0) - new Date(a.date_event || 0);
                    if (!aDate) return -1;
                    if (!bDate) return 1;
                    return new Date(bDate) - new Date(aDate);
                }
                case 'date_completed_asc': {
                    const aDate = a.date_completed || a.completed_at || null;
                    const bDate = b.date_completed || b.completed_at || null;
                    if (!aDate && !bDate) return new Date(a.date_event || 0) - new Date(b.date_event || 0);
                    if (!aDate) return 1;
                    if (!bDate) return -1;
                    return new Date(aDate) - new Date(bDate);
                }
                case 'status_priority': {
                    const pa = STATUS_PRIORITY[a.state] ?? 99;
                    const pb = STATUS_PRIORITY[b.state] ?? 99;
                    if (pa !== pb) return pa - pb;
                    return new Date(a.date_event || 0) - new Date(b.date_event || 0);
                }
                case 'date_event_asc':
                    return new Date(a.date_event || 0) - new Date(b.date_event || 0);
                case 'date_event_desc':
                    return new Date(b.date_event || 0) - new Date(a.date_event || 0);
                case 'bitrix_id_asc':
                    return (a.bitrix_deal?.id || 0) - (b.bitrix_deal?.id || 0);
                case 'bitrix_id_desc':
                    return (b.bitrix_deal?.id || 0) - (a.bitrix_deal?.id || 0);
                default:
                    return 0;
            }
        });

        return result;
    }, [listEvents, filterStatus, sortOrder, searchQuery]);

    return (
        <Box sx={{
            px: { xs: 2, sm: 3, md: 5 },
            pt: { xs: 2, md: 3 },
            pb: { xs: 3, md: 5 },
            maxWidth: 1400,
            mx: 'auto',
        }}>
            {/* ── Заголовок ── */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '14px',
                        background: 'linear-gradient(135deg, #572AC3 0%, #8d6aec 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 14px rgba(87,42,195,0.35)', flexShrink: 0,
                    }}>
                        <CalendarMonthRoundedIcon sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography level="h2" sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.3rem', sm: '1.6rem' },
                            lineHeight: 1.1,
                            background: 'linear-gradient(135deg, #572AC3 0%, #8d6aec 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}>
                            Мои события
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.2 }}>
                            {isCardsView ? `${listEvents.length} событий` : 'Календарь замеров'}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Переключатель вида */}
                    <Box sx={{
                        display: 'flex',
                        background: 'rgba(87,42,195,0.07)',
                        borderRadius: '12px',
                        p: 0.5,
                        gap: 0.5,
                    }}>
                        {viewButtons.map(({ type, icon, title }) => (
                            <IconButton
                                key={type}
                                size="sm"
                                title={title}
                                onClick={() => changeView(type)}
                                sx={{
                                    borderRadius: '9px',
                                    transition: 'all .2s',
                                    background: currentView === type ? 'white' : 'transparent',
                                    boxShadow: currentView === type ? '0 2px 8px rgba(87,42,195,0.15)' : 'none',
                                    color: currentView === type ? '#572AC3' : 'text.secondary',
                                    '&:hover': {
                                        background: currentView === type ? 'white' : 'rgba(87,42,195,0.08)',
                                    },
                                }}
                            >
                                {icon}
                            </IconButton>
                        ))}
                    </Box>

                    {/* Обновить */}
                    <IconButton
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isLoading}
                        sx={{
                            borderRadius: '12px',
                            background: 'rgba(87,42,195,0.07)',
                            color: '#572AC3',
                            transition: 'all .2s',
                            '&:hover': {
                                background: 'rgba(87,42,195,0.14)',
                                transform: 'rotate(180deg)',
                            },
                        }}
                    >
                        {isLoading
                            ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '18px' }} />
                            : <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        }
                    </IconButton>
                </Stack>
            </Stack>

            {/* ── Статистика ── */}
            <StatsBar events={listEvents} />

            {/* ── Контент: карточки или календарь ── */}
            <AnimatePresence mode="wait">
                {isCardsView ? (
                    <motion.div
                        key="cards"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.22 }}
                    >
                        <FilterSortBar
                            filter={filterStatus}
                            sort={sortOrder}
                            search={searchQuery}
                            onFilterChange={setFilterStatus}
                            onSortChange={setSortOrder}
                            onSearchChange={setSearchQuery}
                            totalFiltered={filteredAndSorted.length}
                            totalAll={listEvents.length}
                        />

                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                                <CircularProgress sx={{ '--CircularProgress-progressColor': '#572AC3' }} />
                            </Box>
                        ) : (
                            <CardsView
                                // events={listEvents}
                                events={filteredAndSorted}
                                onComplete={e => navigate(`/surveyor/close/${e?.bitrix_deal?.id}`)}
                                onFail={e => navigate(`/surveyor/fail/${e?.bitrix_deal?.id}`)}
                                onOpen={e => setModalEvent({ open: true, event: e })}
                                onChat={handleChat}
                                onSupplement={e => navigate(surveyorSupplementPath(e))}
                            />
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        key="calendar"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.22 }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: '20px',
                                border: '1.5px solid rgba(87,42,195,0.12)',
                                boxShadow: '0 4px 32px rgba(87,42,195,0.07)',
                                overflow: 'hidden',
                                p: { xs: 1.5, sm: 2.5 },
                                background: 'white',

                                '& .fc': { fontFamily: 'inherit' },
                                '& .fc-toolbar': { mb: '16px !important', flexWrap: 'wrap', gap: '8px' },
                                '& .fc-toolbar-title': { fontSize: { xs: '1rem !important', sm: '1.15rem !important' }, fontWeight: '700 !important', color: '#1a1a2e', textTransform: 'capitalize' },
                                '& .fc-button': { borderRadius: '10px !important', border: 'none !important', background: 'rgba(87,42,195,0.08) !important', color: '#572AC3 !important', fontWeight: '600 !important', fontSize: '0.8rem !important', padding: '6px 12px !important', transition: 'all .2s !important', boxShadow: 'none !important', textTransform: 'none !important' },
                                '& .fc-button:hover': { background: 'rgba(87,42,195,0.18) !important', transform: 'scale(1.02)' },
                                '& .fc-button-active, & .fc-button-primary:not(:disabled).fc-button-active': { background: 'linear-gradient(135deg, #572AC3, #8d6aec) !important', color: 'white !important', boxShadow: '0 3px 10px rgba(87,42,195,0.3) !important' },
                                '& .fc-today-button': { background: 'rgba(87,42,195,0.08) !important', color: '#572AC3 !important' },
                                '& .fc-daygrid-day.fc-day-today': { background: 'rgba(87,42,195,0.05) !important' },
                                '& .fc-daygrid-day-number': { fontWeight: '600', color: '#444', fontSize: '0.82rem' },
                                '& .fc-day-today .fc-daygrid-day-number': { background: 'linear-gradient(135deg, #572AC3, #8d6aec)', color: 'white !important', borderRadius: '8px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px' },
                                '& .fc-col-header-cell': { background: 'rgba(87,42,195,0.04)', borderBottom: '1.5px solid rgba(87,42,195,0.1) !important' },
                                '& .fc-col-header-cell-cushion': { color: '#572AC3 !important', fontWeight: '700 !important', fontSize: '0.78rem !important', textTransform: 'uppercase', letterSpacing: '0.04em', textDecoration: 'none !important', padding: '8px 4px !important' },
                                '& .fc-event': { borderRadius: '7px !important', border: 'none !important', fontSize: '0.76rem !important', fontWeight: '600 !important', padding: '2px 6px !important', cursor: 'pointer', margin: '2px 0px', transition: 'transform .15s, box-shadow .15s !important' },
                                '& .fc-event:hover': { transform: 'scale(1.02)', boxShadow: '0 2px 9px rgba(88, 42, 195, 0.12) !important' },
                                '& .fc-list-event': { cursor: 'pointer' },
                                '& .fc-list-event:hover td': { background: 'rgba(87,42,195,0.05) !important' },
                                '& .fc-list-event-dot': { borderColor: '#572AC3 !important' },
                                '& .fc-list-day-cushion': { background: 'rgba(87,42,195,0.06) !important', color: '#572AC3 !important', fontWeight: '700 !important' },
                                '& .fc-list-empty': { background: 'transparent !important' },
                                '& .fc-scrollgrid': { borderRadius: '12px', overflow: 'hidden', border: '1.5px solid rgba(87,42,195,0.1) !important' },
                                '& .fc-scrollgrid td, & .fc-scrollgrid th': { borderColor: 'rgba(87,42,195,0.08) !important' },
                                '& .fc-more-link': { color: '#572AC3 !important', fontWeight: '700 !important', fontSize: '0.72rem !important' },
                            }}
                        >
                            <FullCalendar
                                ref={calendarRef}
                                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                                initialView={currentView === VIEW_TYPES.CARDS ? VIEW_TYPES.MONTH : currentView}
                                firstDay={1}
                                events={listEvents}
                                eventClick={handleEventClick}
                                datesSet={handleDatesSet}
                                selectable={true}
                                headerToolbar={{
                                    left:   'prev,next today',
                                    center: 'title',
                                    right:  'dayGridMonth,listWeek',
                                }}
                                buttonText={{
                                    dayGridMonth: 'Календарь',
                                    listWeek:     'Список',
                                    today:        'Сегодня',
                                }}
                                height="auto"
                                locale="ru"
                                timeZone="local"
                                fixedWeekCount={false}
                                showNonCurrentDates={false}
                                dayMaxEvents={3}
                            />
                        </Card>

                        <Typography level="body-xs" sx={{ mt: 2, textAlign: 'center', color: 'text.tertiary', fontSize: '0.72rem' }}>
                            Нажмите на событие, чтобы открыть детали
                        </Typography>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}